import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { ActivatedRoute } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { LayoutService } from 'app/layout/layout.service';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import { typeTitleMap } from 'app/shared/models/type-title-map';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { capitalizeFirstLetter } from 'app/shared/Utils/common.utils';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { CustomFieldDialogComponent } from '../../modals/custom-field-dialog/custom-field-dialog.component';
import { CustomFieldImportDialogComponent } from '../../modals/custom-field-import-dialog/custom-field-import-dialog.component';
import { LockedFieldsEditComponent } from '../locked-fields-edit/locked-fields-edit.component';
import {
  CommonAction,
  CustomOrStandardFieldType,
  FieldDefCustomization,
  LockableFieldDef,
  LockableFieldEditMode,
  LockableFieldLockDialogResult,
  LockableFieldsResolverData,
  LockableFieldType,
  routeTypeMap,
} from '../locked-fields.types';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { LockedFieldService } from '../services/locked-fields.service';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { TsPaginatorChangeEvent } from 'tsui';
import { FormControl } from '@angular/forms';
import UrlUtils from '@shared/Utils/url.utils';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';
import { generateDownloadableFile } from '@shared/functions';
import { FieldDef } from '@core/models';
import { AccountService } from '@core/services';
import { SelectionModel } from '@angular/cdk/collections';

type LockedFieldsListParams = { type: RelatedToType };

@Component({
  selector: 'app-locked-fields-list',
  templateUrl: './locked-fields-list.component.html',
  styleUrls: ['./locked-fields-list.component.scss'],
})
export class LockedFieldsListComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  searchValue$: Observable<string>;
  onlyLocked$: Observable<boolean>;
  type: RelatedToType;
  lockableFieldDefs: LockableFieldDef[];
  relatedToTypes: LockableFieldType[];
  displayedColumns = [
    'label',
    'name',
    'type',
    'isCustom',
    'readonly',
    'importable',
    'autoCalculation',
    'options',
    'createdBy',
    'actions',
  ];
  newTable = false;
  columnDefs: ColumnDef[] = [];
  tsActions: TableActions[] = [];
  pagination = { size: 20, page: 0 };
  selectedType: string;

  customObjects: CustomObject[] = [];
  onlyLocked = false;
  fieldMenuOptions: TsMenuItem[] = [
    {
      label: 'Show only locked',
      icon: this.getLockedIcon(),
      action: () => this.toggleOnlyLocked(),
    },
    {
      label: 'Export',
      icon: 'ts_file-export',
      action: () => this.exportFields(),
    },
    {
      label: 'Import',
      icon: 'ts_file-import',
      action: () => this.importFields(),
    },
  ];
  selection = new SelectionModel<LockableFieldDef>(true, []);
  customizations: FieldDefCustomization[] = [];

  @ViewChild('fieldsTable') fieldsTable: MatTable<any>;

  get canLock(): boolean {
    return this.relatedToTypes?.find(({ type }) => type.toLowerCase() === this.type)?.canLock;
  }
  get canInherit(): boolean {
    return this.relatedToTypes?.find(({ type }) => type.toLowerCase() === this.type)?.canInherit;
  }

  readonly onPaginationChange = new Subject<TsPaginatorChangeEvent>();
  readonly searchInputControl: FormControl = new FormControl('');

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly layoutService: LayoutService,
    private readonly dialogService: MatDialog,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly lockedFieldService: LockedFieldService,
    protected readonly store: Store,
    private readonly accountService: AccountService,
  ) {
    super();
  }
  get archivedFields(): LockableFieldDef[] {
    return this.lockableFieldDefs.filter((field) => field.archived);
  }
  get activeFields(): LockableFieldDef[] {
    return this.lockableFieldDefs.filter((field) => !field.archived);
  }
  private resetPagination(): void {
    this.pagination = { page: 0, size: 20 };
  }
  private tablePaginationWatcher(): void {
    this.onPaginationChange.pipe(untilComponentDestroyed(this)).subscribe((event) => {
      this.pagination = {
        page: event.activePage - 1,
        size: event.pageSize,
      };
      this.changeDetectorRef.detectChanges();
    });
  }
  ngOnInit(): void {
    this.lockedFieldService.getFieldDefCustomizations().subscribe((customizations) => {
      this.customizations = customizations;
    });
    this.activatedRoute.params.pipe(untilComponentDestroyed(this)).subscribe(({ type }: LockedFieldsListParams) => {
      this.type = type;
      this.checkTable();
      if (this.newTable) {
        this.setColumnDefs();
        this.setTableActions();
      }
      this.resetPagination();
    });

    this.activatedRoute.parent.data
      .pipe(untilComponentDestroyed(this))
      .subscribe(
        ({ lockableFieldsData: { lockableFields, relatedToTypes, customObjects } }: LockableFieldsResolverData) => {
          this.lockableFieldDefs = lockableFields;
          this.relatedToTypes = relatedToTypes;
          this.customObjects = customObjects;
        },
      );

    this.searchValue$ = this.layoutService.getCommon<string>(CommonAction.fieldSearch).pipe(
      untilComponentDestroyed(this),
      tap(() => this.resetPagination()),
    );

    this.onlyLocked$ = this.layoutService.getCommon<boolean>(CommonAction.onlyLocked).pipe(
      untilComponentDestroyed(this),
      tap(() => this.resetPagination()),
    );

    this.layoutService
      .getCommon(CommonAction.importCustomField)
      .pipe(
        untilComponentDestroyed(this),
        filter((mode: string) => !!mode),
      )
      .subscribe(() => {
        let relatedToType = routeTypeMap[this.type] ?? capitalizeFirstLetter(this.type);

        const isCustomObject = !!this.customObjects
          .filter(({ id }) => id)
          .find(({ name }) => name.toLowerCase() === relatedToType.toLowerCase());

        this.dialogService
          .open(CustomFieldImportDialogComponent, {
            panelClass: 'mailbox-compose-dialog',
            data: { relatedToType, isCustomObject, customObjects: this.customObjects },
          })
          .afterClosed()
          .pipe(untilComponentDestroyed(this))
          .subscribe((result: LockableFieldDef[]) => {
            if (!result) return;

            const newFields = result?.map((fieldDef: LockableFieldDef) => ({ ...fieldDef, lockInfo: [] }));
            const existingFields = this.lockableFieldDefs.map(({ name }) => name);
            const filteredFields = newFields.filter((newfield) => !existingFields.includes(newfield.name));

            this.lockableFieldDefs = [...this.lockableFieldDefs, ...filteredFields];
            this.changeDetectorRef.detectChanges();
          });
      });

    this.layoutService
      .getCommon(CommonAction.addCustomField)
      .pipe(
        untilComponentDestroyed(this),
        filter((mode: string) => !!mode),
      )
      .subscribe(() => {
        const type = routeTypeMap[this.type] ?? capitalizeFirstLetter(this.type);
        let related_to_type = (typeTitleMap[type] ?? type) as RelatedToType;

        if (/ /g.test(related_to_type)) {
          related_to_type = type;
        }

        const isCustomObject = !!this.customObjects
          .filter(({ id }) => id)
          .find(({ name }) => name.toLowerCase() === related_to_type.toLowerCase());

        this.dialogService
          .open(CustomFieldDialogComponent, {
            disableClose: true,
            panelClass: 'mailbox-compose-dialog',
            data: { related_to_type, isCustomObject, mode: 'add' },
          })
          .afterClosed()
          .pipe(
            untilComponentDestroyed(this),
            filter((fieldDef: LockableFieldDef) => !!fieldDef),
          )
          .subscribe((fieldDef: LockableFieldDef) => {
            this.lockableFieldDefs = [...this.lockableFieldDefs, { ...fieldDef, lockInfo: [] }];
            this.layoutService.emitCommon(CommonAction.onFieldAdded, fieldDef, 'subject');
            this.changeDetectorRef.detectChanges();
          });
      });

    this.searchInputControl.valueChanges
      .pipe(untilComponentDestroyed(this), debounceTime(300))
      .subscribe((value: string) => this.layoutService.emitCommon(CommonAction.fieldSearch, value, 'subject'));
  }

  ngAfterViewInit(): void {
    this.tablePaginationWatcher();
  }

  openFieldOptions(
    lockableFieldDef: LockableFieldDef,
    mode: LockableFieldEditMode,
    fieldType?: CustomOrStandardFieldType,
    isBulkEditing?: boolean,
  ): void {
    // Open edit custom field dialog view
    if (mode === 'edit') {
      let related_to_type = (typeTitleMap[lockableFieldDef.related_to_type] ??
        lockableFieldDef.related_to_type) as RelatedToType;

      if (/ /g.test(related_to_type)) {
        related_to_type = lockableFieldDef.related_to_type;
      }

      this.dialogService
        .open(CustomFieldDialogComponent, {
          disableClose: true,
          panelClass: 'mailbox-compose-dialog',
          data: {
            mode,
            fieldType: fieldType,
            ...lockableFieldDef,
            calc_code: lockableFieldDef.meta?.calc_code,
            options: lockableFieldDef.meta?.options,
            readonly: lockableFieldDef.meta?.readonly,
            related_to_type,
          },
        })
        .afterClosed()
        .pipe(
          untilComponentDestroyed(this),
          filter((fieldDef: LockableFieldDef) => !!fieldDef),
        )
        .subscribe((fieldDef: LockableFieldDef) => {
          const index = this.lockableFieldDefs.findIndex(
            ({ related_to_type, name }) => related_to_type === fieldDef.related_to_type && name === fieldDef.name,
          );

          this.lockableFieldDefs[index] = { ...fieldDef, lockInfo: this.lockableFieldDefs[index].lockInfo };
          this.lockableFieldDefs = [...this.lockableFieldDefs];

          this.changeDetectorRef.detectChanges();
        });
    }
    // Open Lock Options dialog view
    else {
      this.dialogService
        .open(LockedFieldsEditComponent, {
          minWidth: '800px',
          maxHeight: '800px',
          data: {
            mode,
            lockableFieldDef,
            customizations: this.customizations,
            fieldsSelection: this.selection.selected,
            isBulkEditing,
          },
        })
        .afterClosed()
        .pipe(
          untilComponentDestroyed(this),
          filter((result: LockableFieldLockDialogResult) => !!result),
        )
        .subscribe((result: LockableFieldLockDialogResult) => {
          const index = this.lockableFieldDefs.findIndex(
            ({ related_to_type, name }) =>
              related_to_type === lockableFieldDef.related_to_type && name === lockableFieldDef.name,
          );

          if (mode === 'lock') {
            this.lockableFieldDefs[index].lockInfo = this.lockableFieldDefs[index].lockInfo.filter(
              ({ id }) => !result.removed.includes(id),
            );
            this.lockableFieldDefs[index].lockInfo = [...this.lockableFieldDefs[index].lockInfo, ...result.added];
          } else {
            this.lockedFieldService.getFieldDefCustomizations().subscribe((res) => {
              this.customizations = res;
            });
          }

          this.lockableFieldDefs = [...this.lockableFieldDefs];
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  toggle(lockableFieldDef: LockableFieldDef, type: 'history' | 'visibility' | 'inherited'): void {
    this.layoutService.emitCommon(CommonAction.setLoading, true, 'subject');

    const enable =
      type === 'history'
        ? !lockableFieldDef.show_history
        : type === 'visibility'
          ? !lockableFieldDef.archived
          : !lockableFieldDef.inherited;

    this.lockedFieldService
      .batchToggle(type, lockableFieldDef.related_to_type, [lockableFieldDef], enable)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        switch (type) {
          case 'history':
            lockableFieldDef.show_history = !lockableFieldDef.show_history;
            break;
          case 'visibility':
            lockableFieldDef.archived = !lockableFieldDef.archived;
            break;
          case 'inherited':
            lockableFieldDef.inherited = !lockableFieldDef.inherited;
            break;
        }

        this.layoutService.emitCommon(CommonAction.setLoading, false, 'subject');
        this.changeDetectorRef.detectChanges();
        this.lockableFieldDefs = [...this.lockableFieldDefs];
        this.setColumnDefs();
      });
  }

  bulkEdit(
    items: LockableFieldDef[],
    type?: 'history' | 'visibility' | 'inherited',
    enable?: boolean,
    mode?: LockableFieldEditMode,
  ): void {
    if (!items.length) {
      return;
    }

    if (!mode) {
      this.layoutService.emitCommon(CommonAction.setLoading, true, 'subject');

      const relatedTo = items[0].related_to_type;

      this.lockedFieldService
        .batchToggle(type, relatedTo, items, enable)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => {
          items.forEach((item) => {
            switch (type) {
              case 'history':
                item.show_history = enable;
                break;
              case 'visibility':
                item.archived = enable;
                break;
              case 'inherited':
                item.inherited = enable;
                break;
            }
          });

          this.layoutService.emitCommon(CommonAction.setLoading, false, 'subject');
          this.changeDetectorRef.detectChanges();
          this.lockableFieldDefs = [...this.lockableFieldDefs];
          this.setColumnDefs();
        });
    }

    if (mode) {
      this.openFieldOptions(items[0], mode, items[0].meta?.is_custom ? 'custom' : 'standard', true);
    }
  }

  filterHasLock(value: LockableFieldDef[], onlyLocked: boolean): any[] {
    if (onlyLocked) {
      return value.filter(({ lockInfo }) => !!lockInfo.length);
    }

    return value;
  }

  eqRelatedToType(value: LockableFieldDef[], type: string): LockableFieldDef[] {
    return value.filter((item) => String(item.related_to_type)?.toLowerCase() === String(type).toLowerCase());
  }

  eqArchived(value: LockableFieldDef[], flag: boolean): LockableFieldDef[] {
    return value.filter((item) => item.archived === flag);
  }

  page(event: PageEvent): void {
    this.pagination = {
      page: event.pageIndex,
      size: event.pageSize,
    };
  }

  setColumnDefs(): void {
    this.columnDefs = [
      {
        tsTableOptions: {
          field: 'label',
          headerName: 'Label',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Label',
        name: 'label',
      },
      {
        tsTableOptions: {
          field: 'name',
          headerName: 'Name',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Name',
        name: 'name',
      },
      {
        tsTableOptions: {
          field: 'type',
          headerName: 'Type',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Type',
        name: 'type',
      },
      {
        tsTableOptions: {
          field: 'isCustom',
          headerName: 'Custom',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          valueGetter: (params) => (params.data.meta?.is_custom ? 'Yes' : 'No'),
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Custom',
        name: 'isCustom',
      },
      {
        tsTableOptions: {
          field: 'readonly',
          headerName: 'Readonly',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          valueGetter: (params) => (params.data.meta?.readonly ? 'Yes' : 'No'),
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Readonly',
        name: 'readonly',
      },
      {
        tsTableOptions: {
          field: 'importable',
          headerName: 'Importable',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          valueGetter: (params) => (params.data?.import ? 'Yes' : 'No'),
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Importable',
        name: 'importable',
      },
      {
        tsTableOptions: {
          field: 'autoCalculation',
          headerName: 'Calculation',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          valueGetter: (params) => params.data.meta?.calc_code || params.data.meta?.calc_description,
          tooltipValueGetter: (params) => params.value,
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },

        type: 'text',
        label: 'Calculation',
        name: 'autoCalculation',
      },
      {
        tsTableOptions: {
          field: 'options',
          headerName: 'Field Options',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          valueGetter: (params) => params.data.meta?.options,
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Options',
        name: 'options',
      },
      {
        tsTableOptions: {
          field: 'createdBy',
          headerName: 'Created By',
          sortable: false,
          resizable: true,
          editable: false,
          suppressHeaderMenuButton: true,
          valueGetter: (params) => (params.data.created_by_user_name ? params.data.created_by_user_name : 'System'),
          cellStyle: LockedFieldsListComponent.applyBackgroundToArchived,
        },
        type: 'text',
        label: 'Created By',
        name: 'createdBy',
      },
    ];
  }

  setTableActions(): void {
    this.tsActions = [
      {
        label: 'Edit Field',
        icon: 'edit',
        action: (item) => this.openFieldOptions(item, 'edit', item.meta?.is_custom ? 'custom' : 'standard'),
      },
      {
        label: 'Lock Options',
        icon: 'ts_lock-alt',
        action: (item) => this.openFieldOptions(item, 'lock', item.meta?.is_custom ? 'custom' : 'standard', false),
        isDisabled: (item) => !this.canLock,
      },
      {
        label: 'Hide Options',
        icon: 'ts_eye',
        action: (item) =>
          this.openFieldOptions(item, 'value_hidden', item.meta?.is_custom ? 'custom' : 'standard', false),
      },
      {
        label: 'Excel Pull Options',
        icon: 'ts_file-import',
        action: (item) =>
          this.openFieldOptions(item, 'excel_pull_locked', item.meta?.is_custom ? 'custom' : 'standard', false),
      },
      {
        label: 'Excel Push Options',
        icon: 'ts_file-export',
        action: (item) =>
          this.openFieldOptions(item, 'excel_push_locked', item.meta?.is_custom ? 'custom' : 'standard', false),
      },
      {
        label: (item) => (item.inherited ? 'Disable Inheritance' : 'Enable Inheritance'),
        icon: (item) => (item.inherited ? 'ts_check-square' : 'ts_square'),
        action: (item) => this.toggle(item, 'inherited'),
        isDisabled: (item) => !this.canInherit,
      },
      {
        label: (item) => (item.show_history ? 'Disable Audit Log' : 'Enable Audit Log'),
        icon: (item) => (item.show_history ? 'ts_check-square' : 'ts_square'),
        action: (item) => this.toggle(item, 'history'),
      },
      {
        label: (item) => (item.archived ? 'Unarchive Field' : 'Archive Field'),
        icon: (item) => (item.archived ? 'ts_eye' : 'ts_eye-slash'),
        action: (item) => this.toggle(item, 'visibility'),
        variant: 'danger',
      },
    ];
  }

  checkTable() {
    this.newTable = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE));
  }

  static applyBackgroundToArchived(params: any) {
    if (params.data && params.data?.archived === true) {
      return { background: '#f5f5f5' };
    }

    return { background: '#ffff' };
  }

  onLookupFieldDropDownChange(relatedToType: LockableFieldType): void {
    this.selectedType = relatedToType.type;
    UrlUtils.saveToLocalStorage('ts_lookup_field_view', relatedToType, true);
  }

  addCustomField(): void {
    this.layoutService.emitCommon(CommonAction.addCustomField, 'create', 'subject');
  }

  toggleOnlyLocked(): void {
    this.onlyLocked = !this.onlyLocked;
    this.layoutService.emitCommon(CommonAction.onlyLocked, this.onlyLocked, 'subject');

    this.fieldMenuOptions[0].icon = this.getLockedIcon();
  }

  exportFields(): void {
    this.layoutService.emitCommon(CommonAction.setLoading, true, 'subject');

    this.accountService
      .getFieldDefs('csv', this.selectedType)
      .pipe(
        untilComponentDestroyed(this),
        filter((data: FieldDef[]) => !!data?.length),
      )
      .subscribe((data: any) => {
        generateDownloadableFile(data, FileFormat.CSV, `field_defs`);
        this.layoutService.emitCommon(CommonAction.setLoading, false, 'subject');
      });
  }

  importFields(): void {
    this.layoutService.emitCommon(CommonAction.importCustomField, 'create', 'subject');
  }

  onSelectionChanged(event) {
    this.selection.setSelection(...event);
  }

  private getLockedIcon(): string {
    return this.onlyLocked ? 'ts_check-square' : 'ts_square';
  }
}
